import { useState } from 'react';
import { ImagePlus } from 'lucide-react';
import { Badge, Button, Icon, PulseDot } from '../ui';
import {
  IMAGE_STATUS_LABEL,
  type ContentImage,
  type ContentImageOption,
} from '../../lib/contentPieces';

/**
 * ImageSlot — one image position in the article preview.
 *
 * queued / generating  → outlined "Image will go here" marker
 * options_ready        → the three Grok options side by side, pick one
 * selected             → the chosen image, with a way back to the options
 *
 * Every state carries a remove × and (once options exist) a manual prompt
 * field that re-queues just this slot.
 */

export interface ImageSlotProps {
  image: ContentImage;
  /** Alt text fallback when the row has no prompt yet. */
  altFallback: string;
  busy?: boolean;
  onRemove: (image: ContentImage) => void;
  onSelect: (image: ContentImage, url: string) => void;
  onManualPrompt: (image: ContentImage, prompt: string) => void;
}

const frame: React.CSSProperties = {
  position: 'relative',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-hairline)',
  background: 'var(--surface-raised)',
  padding: '12px',
  fontFamily: 'var(--font-ui)',
  fontSize: 'var(--text-sm)',
  color: 'var(--text-muted)',
};

function RemoveButton({ onClick, disabled, label }: { onClick: () => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      className="pressable hover-wash"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '24px',
        height: '24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--surface-card)',
        color: 'var(--text-faint)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        zIndex: 2,
      }}
    >
      <Icon name="x" size={12} />
    </button>
  );
}

/**
 * 1200px-wide WebP of the chosen image, via the image-webp function (see netlify/functions).
 * `name` is the file base; the function slugifies it and appends `-<width>w.webp`. Pass the
 * slot's `filename` when the agent set one — falling back to the prompt gives a paragraph
 * slugified and chopped at 80 characters.
 */
const webpDownloadHref = (url: string, name: string, width = 1200) =>
  `/.netlify/functions/image-webp?url=${encodeURIComponent(url)}&w=${width}&name=${encodeURIComponent(name)}`;

function DownloadButton({ href, label }: { href: string; label: string }) {
  return (
    <a
      className="pressable hover-wash"
      aria-label={label}
      title={label}
      href={href}
      download
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '26px',
        height: '26px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--surface-card)',
        color: 'var(--text-muted)',
        boxShadow: 'var(--shadow-rest)',
        zIndex: 2,
      }}
    >
      <Icon name="download" size={13} />
    </a>
  );
}

function ManualPrompt({
  image,
  busy,
  onSubmit,
}: {
  image: ContentImage;
  busy?: boolean;
  onSubmit: (prompt: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(image.prompt_source === 'manual' ? image.prompt || '' : '');

  if (!open) {
    return (
      <Button size="sm" variant="ghost" icon="pencil" onClick={() => setOpen(true)} disabled={busy}>
        Manual prompt
      </Button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: 0 }}>
      <textarea
        className="ck-textarea"
        rows={2}
        value={text}
        autoFocus
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) onSubmit(text);
        }}
        placeholder="Describe the image you want instead. Re-queues just this slot; the old options stay."
      />
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <Button size="sm" variant="secondary" icon="zap" disabled={busy || !text.trim()} onClick={() => onSubmit(text)}>
          Re-queue with this prompt
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function OptionGrid({
  options,
  selectedUrl,
  alt,
  busy,
  onSelect,
}: {
  options: ContentImageOption[];
  selectedUrl: string | null;
  alt: string;
  busy?: boolean;
  onSelect: (url: string) => void;
}) {
  // Options generated before per-option prompts existed carry none; those keep
  // reading off the slot's single prompt, which `alt` already is.
  const showPerOption = options.some((o) => o.prompt);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, options.length))}, minmax(0, 1fr))`,
        gap: '10px',
      }}
    >
      {options.map((opt, i) => {
        const isSel = selectedUrl === opt.url;
        return (
          <div key={`${opt.url}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
            <a
              href={opt.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open the full-resolution original"
              className="ck-slot-thumb"
              style={{
                display: 'block',
                position: 'relative',
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                borderRadius: 'var(--radius-sm)',
                border: `2px solid ${isSel ? 'var(--accent)' : 'var(--border-hairline)'}`,
                background: 'var(--surface-card)',
              }}
            >
              <img
                src={opt.url}
                alt={`${opt.prompt || alt} — option ${i + 1}`}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <span
                className="t-eyebrow"
                style={{
                  position: 'absolute',
                  left: '6px',
                  bottom: '6px',
                  padding: '2px 6px',
                  borderRadius: 'var(--radius-xs)',
                  background: 'rgba(15, 14, 15, 0.7)',
                  color: '#F5F2EF',
                }}
              >
                {opt.width && opt.height ? `${opt.width}×${opt.height}` : `Option ${i + 1}`}
              </span>
            </a>
            {showPerOption ? (
              <span
                title={opt.prompt || undefined}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  minHeight: '3.6em',
                  fontSize: 'var(--text-xs)',
                  lineHeight: 1.2,
                  color: 'var(--text-faint)',
                }}
              >
                {opt.prompt || '—'}
              </span>
            ) : null}
            <Button
              size="sm"
              variant={isSel ? 'primary' : 'secondary'}
              icon={isSel ? 'check' : undefined}
              disabled={busy || isSel}
              onClick={() => onSelect(opt.url)}
              style={{ width: '100%' }}
            >
              {isSel ? 'In use' : 'Use this one'}
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export default function ImageSlot({ image, altFallback, busy, onRemove, onSelect, onManualPrompt }: ImageSlotProps) {
  const [showOptions, setShowOptions] = useState(false);
  const alt =
    image.options.find((o) => o.url === image.selected_url)?.prompt || image.prompt || altFallback;
  const isFeatured = image.kind === 'featured';
  const label = isFeatured ? 'Featured image' : 'Image';
  const removeLabel = isFeatured ? 'Remove featured image' : 'Remove image slot';

  if (image.status === 'queued' || image.status === 'generating') {
    const generating = image.status === 'generating';
    return (
      <div
        style={{
          ...frame,
          border: `1px dashed ${generating ? 'var(--status-info)' : 'var(--border-strong)'}`,
          background: generating ? 'var(--status-info-soft)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minHeight: '72px',
        }}
      >
        <span
          style={{
            width: '34px',
            height: '34px',
            borderRadius: 'var(--radius-sm)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-active)',
            color: 'var(--text-muted)',
            flexShrink: 0,
          }}
        >
          <ImagePlus size={16} strokeWidth={1.75} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'var(--text-body)', fontWeight: 500 }}>
              {isFeatured ? 'Featured image will go here' : 'Image will go here'}
            </span>
            <Badge tone={generating ? 'info' : 'warn'} dot pulse={generating}>
              {IMAGE_STATUS_LABEL[image.status]}
            </Badge>
          </span>
          <span style={{ display: 'block', marginTop: '3px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {image.prompt_source === 'manual' && image.prompt
              ? `Manual prompt: ${image.prompt}`
              : generating
                ? 'Grok is drawing three options from the copy above this spot.'
                : 'Sent with the next “Create images”. The agent writes the prompt from the copy above.'}
          </span>
        </span>
        <RemoveButton onClick={() => onRemove(image)} disabled={busy} label={removeLabel} />
      </div>
    );
  }

  if (image.status === 'options_ready') {
    // Each option can carry its own prompt. When they differ, the single row prompt
    // above the grid would be a lie, so say where the prompts are instead.
    const optionPrompts = image.options.map((o) => o.prompt).filter(Boolean) as string[];
    const perOption = optionPrompts.length > 1 && new Set(optionPrompts).size > 1;
    return (
      <div style={{ ...frame, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingRight: '32px' }}>
          <PulseDot tone="up" />
          <span style={{ color: 'var(--text-body)', fontWeight: 500 }}>{label}: pick one of three</span>
          {image.prompt ? (
            <span
              title={image.prompt}
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-faint)',
              }}
            >
              {perOption
                ? `${optionPrompts.length} different prompts — one under each option`
                : `${image.prompt_source === 'manual' ? 'Manual' : 'Agent'} prompt: ${image.prompt}`}
            </span>
          ) : null}
        </div>
        <OptionGrid options={image.options} selectedUrl={null} alt={alt} busy={busy} onSelect={(url) => onSelect(image, url)} />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <ManualPrompt image={image} busy={busy} onSubmit={(p) => onManualPrompt(image, p)} />
        </div>
        <RemoveButton onClick={() => onRemove(image)} disabled={busy} label={removeLabel} />
      </div>
    );
  }

  // selected
  return (
    <figure style={{ margin: 0, position: 'relative' }}>
      {image.selected_url ? (
        <div style={{ position: 'relative' }}>
          <img
            src={image.selected_url}
            alt={alt}
            style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 'var(--radius-md)' }}
          />
          <DownloadButton
            href={webpDownloadHref(image.selected_url, image.filename || `${image.slot_key}-${alt}`)}
            label={`Download 1200px WebP${image.filename ? ` — ${image.filename}-1200w.webp` : ''}`}
          />
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Badge tone="up">{isFeatured ? 'Featured' : 'Placed'}</Badge>
        {image.prompt ? (
          <span
            title={image.prompt}
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
            }}
          >
            {image.prompt}
          </span>
        ) : null}
        {image.options.length > 0 ? (
          <Button size="sm" variant="ghost" icon="refresh-cw" disabled={busy} onClick={() => setShowOptions((v) => !v)}>
            {showOptions ? 'Hide options' : 'Change'}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" icon="trash-2" disabled={busy} onClick={() => onRemove(image)}>
          Remove
        </Button>
      </div>
      {showOptions ? (
        <div style={{ ...frame, marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <OptionGrid
            options={image.options}
            selectedUrl={image.selected_url}
            alt={alt}
            busy={busy}
            onSelect={(url) => onSelect(image, url)}
          />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <ManualPrompt image={image} busy={busy} onSubmit={(p) => onManualPrompt(image, p)} />
          </div>
        </div>
      ) : null}
    </figure>
  );
}
